export * from './access';
export const PRODUCT_NAME = 'AOT OpsAI';
export { canonicalMetricId, customerMetricLabel, customerTemperatureLabel, customerFinding } from './metric-presentation';
export const DEFAULT_TENANT_ID = 'tenant_aot_internal';
export const DEFAULT_SITE_ID = 'site_aot_lab';
export const utcNow = () => new Date().toISOString();

export const USER_TIMEZONE_MODES = ['AUTO_BROWSER', 'WORKSPACE_DEFAULT', 'SPECIFIC'] as const;
export type UserTimezoneMode = (typeof USER_TIMEZONE_MODES)[number];
export const SITE_TIMEZONE_MODES = ['INHERIT_WORKSPACE', 'SPECIFIC'] as const;
export type SiteTimezoneMode = (typeof SITE_TIMEZONE_MODES)[number];
export const INTEGRATION_TIMEZONE_MODES = ['INHERIT_SITE', 'SPECIFIC'] as const;
export type IntegrationTimezoneMode = (typeof INTEGRATION_TIMEZONE_MODES)[number];

export type TimezonePreference = {
  mode?: UserTimezoneMode | null;
  timeZone?: string | null;
};

export function isIanaTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || value.length < 3 || value.length > 100) return false;
  if (/^(?:GMT|UTC)[+-]/i.test(value)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(0);
    return value === 'UTC' || value.includes('/');
  } catch {
    return false;
  }
}

export function resolveDisplayTimeZone(input: {
  override?: string | null;
  preference?: TimezonePreference | null;
  workspaceTimeZone?: string | null;
  browserTimeZone?: string | null;
}): string {
  if (isIanaTimeZone(input.override)) return input.override;
  const preference = input.preference;
  if (preference?.mode === 'SPECIFIC' && isIanaTimeZone(preference.timeZone))
    return preference.timeZone;
  if (preference?.mode === 'WORKSPACE_DEFAULT' && isIanaTimeZone(input.workspaceTimeZone))
    return input.workspaceTimeZone;
  if ((preference?.mode ?? 'AUTO_BROWSER') === 'AUTO_BROWSER') {
    if (isIanaTimeZone(input.browserTimeZone)) return input.browserTimeZone;
    if (isIanaTimeZone(input.workspaceTimeZone)) return input.workspaceTimeZone;
  }
  if (isIanaTimeZone(input.workspaceTimeZone)) return input.workspaceTimeZone;
  if (isIanaTimeZone(input.browserTimeZone)) return input.browserTimeZone;
  return 'UTC';
}

export function resolveSiteTimeZone(input: {
  mode?: SiteTimezoneMode | null;
  timeZone?: string | null;
  workspaceTimeZone?: string | null;
}): string {
  return input.mode === 'SPECIFIC' && isIanaTimeZone(input.timeZone)
    ? input.timeZone
    : isIanaTimeZone(input.workspaceTimeZone)
      ? input.workspaceTimeZone
      : 'UTC';
}

export function resolveIntegrationTimeZone(input: {
  mode?: IntegrationTimezoneMode | null;
  sourceTimeZone?: string | null;
  siteTimeZone?: string | null;
}): string {
  return input.mode === 'SPECIFIC' && isIanaTimeZone(input.sourceTimeZone)
    ? input.sourceTimeZone
    : isIanaTimeZone(input.siteTimeZone)
      ? input.siteTimeZone
      : 'UTC';
}

export function timeZoneOffsetMinutes(at: string | number | Date, timeZone: string): number {
  const date = at instanceof Date ? at : new Date(at);
  if (!Number.isFinite(date.getTime()) || !isIanaTimeZone(timeZone)) return 0;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const representedUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );
  return Math.round((representedUtc - date.getTime()) / 60_000);
}

export function formatUtcOffset(minutes: number): string {
  const sign = minutes >= 0 ? '+' : '-';
  const absolute = Math.abs(minutes);
  return `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`;
}

function zonedMidnightUtc(year: number, month: number, day: number, timeZone: string) {
  const wallClockUtc = Date.UTC(year, month - 1, day);
  let candidate = wallClockUtc - timeZoneOffsetMinutes(wallClockUtc, timeZone) * 60_000;
  candidate = wallClockUtc - timeZoneOffsetMinutes(candidate, timeZone) * 60_000;
  return new Date(candidate);
}

/** UTC bounds for a calendar day in an IANA timezone, including 23/25-hour DST days. */
export function zonedCalendarDayBounds(at: string | number | Date, timeZone: string) {
  const date = at instanceof Date ? at : new Date(at);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: isIanaTimeZone(timeZone) ? timeZone : 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const year = Number(values.year);
  const month = Number(values.month);
  const day = Number(values.day);
  const next = new Date(Date.UTC(year, month - 1, day + 1));
  return {
    startUtc: zonedMidnightUtc(year, month, day, timeZone).toISOString(),
    endUtc: zonedMidnightUtc(
      next.getUTCFullYear(),
      next.getUTCMonth() + 1,
      next.getUTCDate(),
      timeZone,
    ).toISOString(),
  };
}

export const AOT_OPERATIONS_EDGE_NAME = 'AOT Operations Edge';
export const AOT_OPERATIONS_SITE_NAME = 'AOT Operations Site';
const AOT_INTERNAL_EDGE_NAMES = new Set(['AOT Internal Pilot Edge']);
const AOT_INTERNAL_SITE_NAMES = new Set(['AOT Internal Pilot']);
const AOT_INTERNAL_SITE_IDS = new Set(['site_ffa53f9c720346988678b92e96dbdd01']);

type EdgePresentationInput = {
  tenantId?: unknown;
  name?: unknown;
  displayName?: unknown;
  deploymentType?: unknown;
};

/**
 * Returns the customer-facing label for an Edge resource without changing its
 * enrolled name or immutable identity. Exact legacy AOT names are treated as
 * explicit internal-resource classification, not as a general word filter.
 */
export function customerFacingEdgeName(input: EdgePresentationInput): string {
  const displayName = String(input.displayName ?? '').trim();
  if (displayName) return displayName;

  const name = String(input.name ?? '').trim();
  if (input.deploymentType === 'AOT_EDGE_MAIN') return 'AOT Edge Main';
  if (
    AOT_INTERNAL_EDGE_NAMES.has(name) &&
    (input.tenantId == null || input.tenantId === DEFAULT_TENANT_ID)
  )
    return AOT_OPERATIONS_EDGE_NAME;
  return name;
}

type SitePresentationInput = {
  tenantId?: unknown;
  siteId?: unknown;
  name?: unknown;
  displayName?: unknown;
};

/**
 * Returns the customer-facing label for a Site without changing its stored
 * name or immutable identity. Only exact, known AOT-internal legacy names are
 * mapped; arbitrary customer-created names such as "Pilot Plant" are kept.
 */
export function customerFacingSiteName(input: SitePresentationInput): string {
  const displayName = String(input.displayName ?? '').trim();
  if (displayName) return displayName;

  const name = String(input.name ?? '').trim();
  const siteId = String(input.siteId ?? '').trim();
  if (
    AOT_INTERNAL_SITE_NAMES.has(name) &&
    (input.tenantId === DEFAULT_TENANT_ID || AOT_INTERNAL_SITE_IDS.has(siteId))
  )
    return AOT_OPERATIONS_SITE_NAME;
  return name;
}
